import { TAG_TYPES, accountMbPeriodsTag, siteMaterialsTag, siteMbPeriodTag } from '@/lib/api/tags';
import {
  buildListQueryParams,
  handledError,
  processListPage,
  processServerError,
} from '@/lib/api/listQuery';
import { notifyOnSuccess } from '@/lib/api/notifyOnSuccess';
import { SITE_MATERIAL_TAB, SITE_SORT_FIELDS, SITE_SEARCH_IN } from '../constants/sites.constants';
import {
  processAccountMbPeriods,
  processAccountTerminals,
  processSite,
  processSiteDetail,
  processSiteMaterials,
  processSiteMbPeriod,
  processSiteForm,
  buildAssignMaterialsDto,
  buildMbPeriodDto,
  buildSiteDto,
} from '../mappers/sitesMappers';

const PATHS = {
  list: '/sites',
  site: (id) => `/sites/${id}`,
  terminals: '/terminals',
  terminal: (id) => `/terminals/${id}`,
  siteMaterials: (siteId) => `/sites/materials/${siteId}`,
  siteMaterial: (siteId, materialId) => `/sites/${siteId}/material/${materialId}`,
  mbPeriods: '/mb-periods',
  mbPeriod: (id) => `/mb-periods/${id}`,
  siteMbPeriod: (siteId) => `/mb-periods/site/${siteId}`,
};

export const sitesEndpoints = (build) => ({
  getSites: build.query({
    query: (params) => ({
      url: PATHS.list,
      params: buildListQueryParams(params, {
        sortFields: SITE_SORT_FIELDS,
        searchIn: SITE_SEARCH_IN,
      }),
    }),
    transformResponse: (dto) => processListPage(dto, 'sites', processSite),
    providesTags: (result) =>
      result
        ? [
            ...result.rows.map((row) => ({ type: TAG_TYPES.Site, id: row.id })),
            { type: TAG_TYPES.Site, id: 'LIST' },
          ]
        : [{ type: TAG_TYPES.Site, id: 'LIST' }],
  }),

  getSiteDetail: build.query({
    query: (id) => ({ url: PATHS.site(id) }),
    transformResponse: (dto) => processSiteDetail(dto?.output?.data),
    transformErrorResponse: handledError,
    providesTags: (result, error, id) => [{ type: TAG_TYPES.Site, id }],
  }),

  getSiteMaterials: build.query({
    query: (id) => ({ url: PATHS.site(id), params: { tab: SITE_MATERIAL_TAB } }),
    transformResponse: (dto) => processSiteMaterials(dto?.output?.[SITE_MATERIAL_TAB]),
    transformErrorResponse: handledError,
    providesTags: (result, error, id) => [siteMaterialsTag(id)],
  }),

  getAccountTerminals: build.query({
    query: () => ({ url: PATHS.terminals }),
    transformResponse: (dto) => processAccountTerminals(dto?.output?.terminals),
    transformErrorResponse: handledError,
    providesTags: [{ type: TAG_TYPES.Site, id: 'TERMINALS' }],
  }),

  getAccountMbPeriods: build.query({
    query: () => ({ url: PATHS.mbPeriods, params: { limit: 1 } }),
    transformResponse: (dto) => processAccountMbPeriods(dto?.output),
    transformErrorResponse: handledError,
    providesTags: [accountMbPeriodsTag],
  }),

  getSiteMbPeriod: build.query({
    query: ({ siteId, terminalId }) => ({
      url: PATHS.siteMbPeriod(siteId),
      ...(terminalId ? { params: { terminalId } } : {}),
    }),
    transformResponse: (dto) => processSiteMbPeriod(dto?.output?.data),
    transformErrorResponse: handledError,
    providesTags: (result, error, arg) => [siteMbPeriodTag(arg.siteId)],
  }),

  createMbPeriod: build.mutation({
    query: ({ values }) => ({
      url: PATHS.mbPeriods,
      method: 'POST',
      body: buildMbPeriodDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('SITES_MB_PERIOD_SAVED'),
    invalidatesTags: (result, error, arg) => [siteMbPeriodTag(arg.siteId), accountMbPeriodsTag],
  }),

  updateMbPeriod: build.mutation({
    query: ({ id, values }) => ({
      url: PATHS.mbPeriod(id),
      method: 'PATCH',
      body: buildMbPeriodDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('SITES_MB_PERIOD_SAVED'),
    invalidatesTags: (result, error, arg) => [siteMbPeriodTag(arg.siteId), accountMbPeriodsTag],
  }),

  assignSiteMaterials: build.mutation({
    query: ({ siteId, values }) => ({
      url: PATHS.siteMaterials(siteId),
      method: 'POST',
      body: buildAssignMaterialsDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('MATERIALS_ASSIGNED_TO_TERMINAL'),
    invalidatesTags: (result, error, arg) => [
      siteMaterialsTag(arg.siteId),
      { type: TAG_TYPES.Site, id: arg.siteId },
      { type: TAG_TYPES.Site, id: 'LIST' },
    ],
  }),

  removeSiteMaterial: build.mutation({
    query: ({ siteId, materialId, terminalId }) => ({
      url: PATHS.siteMaterial(siteId, materialId),
      method: 'DELETE',
      params: { terminalId },
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('MATERIALS_REMOVED_FROM_TERMINAL'),
    invalidatesTags: (result, error, arg) => [siteMaterialsTag(arg.siteId)],
  }),

  getSiteForm: build.query({
    query: (id) => ({ url: PATHS.site(id) }),
    transformResponse: (dto) => processSiteForm(dto?.output?.data),
    transformErrorResponse: handledError,
    providesTags: (result, error, id) => [{ type: TAG_TYPES.Site, id }],
  }),
  createSite: build.mutation({
    query: (values) => ({
      url: PATHS.list,
      method: 'POST',
      body: buildSiteDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('SITES_SAVED'),
    invalidatesTags: [{ type: TAG_TYPES.Site, id: 'LIST' }],
  }),

  createTerminal: build.mutation({
    query: (body) => ({ url: PATHS.terminals, method: 'POST', body }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('SITES_TERMINAL_SAVED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Site, id: arg.siteId },
      { type: TAG_TYPES.Site, id: 'LIST' },
    ],
  }),

  updateTerminal: build.mutation({
    query: ({ id, ...body }) => ({ url: PATHS.terminal(id), method: 'PATCH', body }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('SITES_TERMINAL_SAVED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Site, id: arg.siteId },
      { type: TAG_TYPES.Site, id: 'LIST' },
    ],
  }),

  deleteTerminal: build.mutation({
    query: ({ id }) => ({ url: PATHS.terminal(id), method: 'DELETE' }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('SITES_TERMINAL_DELETED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Site, id: arg.siteId },
      { type: TAG_TYPES.Site, id: 'LIST' },
    ],
  }),

  updateSite: build.mutation({
    query: ({ id, values }) => ({
      url: PATHS.site(id),
      method: 'PATCH',
      body: buildSiteDto(values),
    }),
    transformErrorResponse: processServerError,
    onQueryStarted: notifyOnSuccess('SITES_SAVED'),
    invalidatesTags: (result, error, arg) => [
      { type: TAG_TYPES.Site, id: arg.id },
      { type: TAG_TYPES.Site, id: 'LIST' },
    ],
  }),
});
